import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import toast from 'react-hot-toast';

interface UserFormData {
  nome: string;
  email: string;
  senha: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'USER';
  tenantIds: string[];
  ativo: boolean;
}

interface TenantOption {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  users?: Array<{ email?: string }>;
}

export function CreateUserPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'novo';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [availableTenants, setAvailableTenants] = useState<TenantOption[]>([]);
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');

  const [formData, setFormData] = useState<UserFormData>({
    nome: '',
    email: '',
    senha: '',
    role: 'USER',
    tenantIds: [],
    ativo: true
  });

  const loadTenants = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/tenants', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableTenants(data.tenants || []);
      }
    } catch (e) {
      console.error('Erro ao carregar empresas:', e);
      setAvailableTenants([]);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    if (!isNew && id) {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      Promise.all([
        fetch(`/api/users/${id}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`/api/user-tenants/users/${id}/tenants`, { headers: { 'Authorization': `Bearer ${token}` } })
      ])
        .then(async ([userRes, tenantsRes]) => {
          if (!userRes.ok) {
            toast.error('Usuário não encontrado');
            navigate('/superadmin/usuarios');
            return;
          }
          const userData = await userRes.json();
          const user = userData.data?.user ?? userData.user;
          let userTenantIds: string[] = [];
          if (tenantsRes.ok) {
            const tenantsData = await tenantsRes.json();
            userTenantIds = (tenantsData.tenants || []).map((t: { id: string }) => t.id);
          }
          setFormData({
            nome: user.nome ?? '',
            email: user.email ?? '',
            senha: '',
            role: (user.role as 'SUPERADMIN' | 'ADMIN' | 'USER') ?? 'USER',
            tenantIds: userTenantIds,
            ativo: user.ativo ?? true
          });
        })
        .finally(() => setLoading(false));
    }
  }, [isNew, id, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nome || !formData.email) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (isNew && !formData.senha) {
      toast.error('Informe a senha do usuário');
      return;
    }
    if (formData.role !== 'SUPERADMIN' && formData.tenantIds.length === 0) {
      toast.error('Selecione pelo menos uma empresa');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = isNew ? '/api/users' : `/api/users/${id}`;
      const method = isNew ? 'POST' : 'PUT';
      const body: Record<string, unknown> = {
        nome: formData.nome,
        email: formData.email,
        role: formData.role,
        ativo: formData.ativo
      };
      if (formData.senha) body.senha = formData.senha;
      if (formData.role !== 'SUPERADMIN' && formData.tenantIds.length > 0) {
        body.tenantId = formData.tenantIds[0];
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const err = await response.json();
        if (err.upgradeRequired || (err.message && err.message.includes('Limite'))) {
          toast.error(err.message || 'Limite de usuários atingido.', { duration: 6000, icon: '⚠️' });
          return;
        }
        throw new Error(err.message || `Erro ao ${isNew ? 'criar' : 'atualizar'} usuário`);
      }

      const result = await response.json();
      const userId = isNew ? result.data?.user?.id : id;
      if (userId) {
        if (!isNew) {
          const existingRes = await fetch(`/api/user-tenants/users/${userId}/tenants`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (existingRes.ok) {
            const existingData = await existingRes.json();
            const currentIds = (existingData.tenants || []).map((t: { id: string }) => t.id);
            for (const tenantId of currentIds) {
              if (!formData.tenantIds.includes(tenantId)) {
                await fetch(`/api/user-tenants/associations/${userId}/${tenantId}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${token}` }
                }).catch(() => {});
              }
            }
          }
        }
        for (const tenantId of formData.tenantIds) {
          await fetch('/api/user-tenants/associations', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ userId, tenantId, role: formData.role })
          }).catch(() => {});
        }
      }

      toast.success(`Usuário ${isNew ? 'criado' : 'atualizado'} com sucesso!`);
      navigate('/superadmin/usuarios');
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const q = (tenantSearchQuery || '').trim().toLowerCase();
  const filteredTenants = availableTenants.filter((t) => {
    if (!q) return true;
    const matchName = t.name.toLowerCase().includes(q);
    const matchSlug = t.slug.toLowerCase().includes(q);
    const matchEmail = t.users?.some((u) => u.email?.toLowerCase().includes(q)) ?? false;
    return matchName || matchSlug || matchEmail;
  });

  if (!isNew && loading) {
    return (
      <>
        <Header title="Usuários" subtitle="Carregando..." compactTopbar />
        <PageLayout>
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Header title="Usuários" subtitle={isNew ? 'Novo usuário' : 'Editar usuário'} compactTopbar />
      <PageLayout>
        <PageHeader
          title={isNew ? 'Novo usuário' : 'Editar usuário'}
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'SuperAdmin', href: '/superadmin/dashboard' },
            { label: 'Usuários', href: '/superadmin/usuarios' },
            { label: isNew ? 'Novo' : 'Editar' },
          ]}
        />
        <div className="space-y-8 w-full">
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">
                {isNew ? 'Novo usuário' : 'Editar usuário'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Nome completo *
                    </label>
                    <input
                      id="nome"
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      placeholder="Nome completo do usuário"
                      className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      E-mail *
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@exemplo.com"
                      className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="senha" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Senha {isNew ? '*' : '(deixe em branco para não alterar)'}
                    </label>
                    <input
                      id="senha"
                      type="password"
                      value={formData.senha}
                      onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                      placeholder={isNew ? 'Senha segura' : 'Deixe em branco para manter'}
                      className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      required={isNew}
                      minLength={isNew ? 6 : undefined}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Mínimo 6 caracteres; use maiúsculas, minúsculas e números.
                    </p>
                  </div>
                  <div>
                    <label htmlFor="role" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Nível de acesso *
                    </label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({
                        ...formData,
                        role: e.target.value as 'SUPERADMIN' | 'ADMIN' | 'USER'
                      })}
                      className="input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      <option value="USER">Usuário</option>
                      <option value="ADMIN">Administrador</option>
                      <option value="SUPERADMIN">Super Administrador (acesso global)</option>
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {formData.role === 'SUPERADMIN'
                        ? 'Super Admin tem acesso a todas as empresas. Você pode opcionalmente associá-lo a empresas.'
                        : 'Defina o nível de acesso nas empresas selecionadas.'}
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700 lg:pt-0 lg:border-t-0">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Empresas {formData.role === 'SUPERADMIN' ? '(opcional)' : '*'}
                    </h3>
                    {availableTenants.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600">
                        Nenhuma empresa disponível
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="relative">
                          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
                          <input
                            type="text"
                            placeholder="Buscar por nome, slug ou e-mail da empresa..."
                            value={tenantSearchQuery}
                            onChange={(e) => setTenantSearchQuery(e.target.value)}
                            className="input-field w-full pl-9 text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                        </div>
                        {filteredTenants.length > 0 && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setFormData({
                                ...formData,
                                tenantIds: [...new Set([...formData.tenantIds, ...filteredTenants.map((t) => t.id)])]
                              })}
                              className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
                            >
                              Selecionar todas
                            </button>
                            <span className="text-gray-300 dark:text-gray-600">|</span>
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, tenantIds: [] })}
                              className="text-sm text-gray-600 dark:text-gray-400 hover:underline font-medium"
                            >
                              Deselecionar todas
                            </button>
                          </div>
                        )}
                        <div className="border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden bg-white dark:bg-gray-800/50 max-h-[280px] overflow-y-auto">
                          <div className="p-2 space-y-1">
                            {filteredTenants.length === 0 ? (
                              <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
                                Nenhuma empresa encontrada
                              </div>
                            ) : (
                              filteredTenants.map((tenant) => {
                                const isSelected = formData.tenantIds.includes(tenant.id);
                                return (
                                  <label
                                    key={tenant.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                      isSelected
                                        ? 'bg-emerald-50 dark:bg-emerald-500/15 border border-emerald-200 dark:border-emerald-500/30'
                                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 border border-transparent'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setFormData({
                                            ...formData,
                                            tenantIds: [...formData.tenantIds, tenant.id]
                                          });
                                        } else {
                                          setFormData({
                                            ...formData,
                                            tenantIds: formData.tenantIds.filter((tid) => tid !== tenant.id)
                                          });
                                        }
                                      }}
                                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium text-gray-900 dark:text-white/90 truncate">
                                          {tenant.name}
                                        </span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                                          tenant.active
                                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                            : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                                        }`}>
                                          {tenant.active ? 'Ativa' : 'Inativa'}
                                        </span>
                                      </div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{tenant.slug}</span>
                                    </div>
                                  </label>
                                );
                              })
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formData.tenantIds.length > 0 && (
                            <span className="font-medium text-emerald-600 dark:text-emerald-400">
                              {formData.tenantIds.length} empresa(s) selecionada(s).
                            </span>
                          )}
                          {formData.role === 'SUPERADMIN' ? ' Deixe vazio para acesso global.' : ' Selecione pelo menos uma empresa.'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center gap-3">
                    <input
                      id="ativo"
                      type="checkbox"
                      checked={formData.ativo}
                      onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500"
                    />
                    <label htmlFor="ativo" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Usuário ativo
                    </label>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/superadmin/usuarios')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
                >
                  <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin text-base shrink-0" aria-hidden />
                      Salvando...
                    </>
                  ) : isNew ? (
                    <>
                      <i className="fas fa-plus text-base shrink-0" aria-hidden />
                      Criar usuário
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check text-base shrink-0" aria-hidden />
                      Salvar alterações
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </PageLayout>
    </>
  );
}
